import { Icon } from '@iconify/react'
import { Spin } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { useNotification } from 'app/providers'
import classNames from 'classnames'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { DateService } from 'common/services/dateService'
import { stringifyResponseForError } from 'common/utils/text.utils'
import {
  useFetchSoapNotesQuery,
  useSaveSoapNoteMutation,
} from 'features/Procedure/state/procedure.api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ProcedureImageList } from './components/procedure-image-list'
import styles from './notes.module.scss'

type Note = {
  subjective: string
  objective: string
  assessment: string
}

type NoteFormValues = {
  subjective: string
  objective: string
  assessment: string
}

type DirtyFields = Record<keyof NoteFormValues, boolean>

type SectionSaveStatus = 'idle' | 'saving' | 'saved' | 'error'
type SectionSaveStatuses = Record<keyof NoteFormValues, SectionSaveStatus>
type SectionLastSavedAt = Record<keyof NoteFormValues, string | null>

type NoteDraftState = {
  values: NoteFormValues
  dirtyFields: DirtyFields
  lastSavedValues: NoteFormValues
}

type ActiveNoteContext = {
  patientPid: number
  encounterUid: string
  encounterKey: string
}

type PendingDraftSaveSnapshot = {
  context: ActiveNoteContext
  draft: NoteDraftState
  preferredField: keyof NoteFormValues | null
}

const NOTES_AUTOSAVE_DELAY_MS = 2000
const NOTES_SAVING_INDICATOR_DELAY_MS = 1000

const emptyFormValues: NoteFormValues = {
  subjective: '',
  objective: '',
  assessment: '',
}

const sectionKeys: (keyof NoteFormValues)[] = ['subjective', 'objective', 'assessment']
const sectionLabels: Record<keyof NoteFormValues, string> = {
  subjective: 'Patient History',
  objective: 'Physical Exam',
  assessment: 'Assessment / Plan',
}

const createEmptyDirtyFields = (): DirtyFields => ({
  subjective: false,
  objective: false,
  assessment: false,
})

const createEmptySectionSaveStatuses = (): SectionSaveStatuses => ({
  subjective: 'idle',
  objective: 'idle',
  assessment: 'idle',
})

const createEmptySectionLastSavedAt = (): SectionLastSavedAt => ({
  subjective: null,
  objective: null,
  assessment: null,
})

const createEmptyDraftState = (): NoteDraftState => ({
  values: { ...emptyFormValues },
  dirtyFields: createEmptyDirtyFields(),
  lastSavedValues: { ...emptyFormValues },
})

const cloneDraftState = (draft: NoteDraftState): NoteDraftState => ({
  values: { ...draft.values },
  dirtyFields: { ...draft.dirtyFields },
  lastSavedValues: { ...draft.lastSavedValues },
})

const normalizeFieldValueForSave = (value: string): string =>
  value.trim().length === 0 ? '' : value

const isFieldValueSavable = (value: string): boolean => {
  const normalizedValue = normalizeFieldValueForSave(value)
  return normalizedValue.length === 0 || normalizedValue.length >= 2
}

const getSavableDirtyFields = (draft: NoteDraftState): (keyof NoteFormValues)[] =>
  sectionKeys.filter(
    (field) => draft.dirtyFields[field] && isFieldValueSavable(draft.values[field]),
  )

const getPreferredSavableField = (
  draft: NoteDraftState,
  preferredField: keyof NoteFormValues | null,
): keyof NoteFormValues | null => {
  const savableFields = getSavableDirtyFields(draft)

  if (savableFields.length === 0) {
    return null
  }

  if (preferredField && savableFields.includes(preferredField)) {
    return preferredField
  }

  return savableFields[0]
}

const getDraftPayload = (
  draft: NoteDraftState,
  fields: (keyof NoteFormValues)[],
): Partial<NoteFormValues> => {
  const payload: Partial<NoteFormValues> = {}

  fields.forEach((field) => {
    payload[field] = normalizeFieldValueForSave(draft.values[field])
  })

  return payload
}

const getAutosaveErrorDescription = (error: unknown): string => {
  const fallbackMessage = 'Unable to autosave note. Your draft is still preserved.'
  const errorRecord = (error && typeof error === 'object' ? error : null) as {
    message?: unknown
    data?: unknown
  } | null
  const dataRecord = (
    errorRecord?.data && typeof errorRecord.data === 'object' ? errorRecord.data : null
  ) as {
    message?: unknown
  } | null

  if (typeof dataRecord?.message === 'string' && dataRecord.message.length > 0) {
    return dataRecord.message
  }

  if (typeof errorRecord?.message === 'string' && errorRecord.message.length > 0) {
    return errorRecord.message
  }

  return `${fallbackMessage} Response: ${stringifyResponseForError(errorRecord?.data ?? error)}`
}

const toActiveNoteContext = (
  selectedPatientId: number | null,
  selectedEncounterUid: string | null,
): ActiveNoteContext | null => {
  if (!selectedPatientId || !selectedEncounterUid) {
    return null
  }

  return {
    patientPid: selectedPatientId,
    encounterUid: selectedEncounterUid,
    encounterKey: `${selectedPatientId}:${selectedEncounterUid}`,
  }
}

export type NotesProps = {
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  disableActions?: boolean
  className?: string
  defaultExpanded?: boolean
  onRegisterLeaveAction?: ((handler: (() => Promise<void>) | null) => void) | undefined
}

export const Notes = ({
  selectedPatientId,
  selectedEncounterUid,
  disableActions = false,
  className,
  defaultExpanded = true,
  onRegisterLeaveAction,
}: NotesProps) => {
  const { setNotification } = useNotification()

  const activeContext = useMemo(
    () => toActiveNoteContext(selectedPatientId, selectedEncounterUid),
    [selectedPatientId, selectedEncounterUid],
  )
  const activeEncounterKey = activeContext?.encounterKey ?? null
  const canMutate = Boolean(activeContext && !disableActions)
  const shouldSkipSoapQuery = !activeContext

  const {
    currentData: soapNotesData,
    isLoading: isLoadingSoapNotes,
    isFetching: isFetchingSoapNotes,
    isUninitialized: isSoapNotesUninitialized,
  } = useFetchSoapNotesQuery(
    {
      patientPid: selectedPatientId ?? 0,
      encounterUid: selectedEncounterUid ?? '',
    },
    {
      skip: shouldSkipSoapQuery,
    },
  )

  const [saveSoapNote] = useSaveSoapNoteMutation()

  const notes = useMemo<Note[]>(() => {
    if (!soapNotesData) return []

    return soapNotesData.map((note) => ({
      subjective: note.subjective ?? '',
      objective: note.objective ?? '',
      assessment: note.assessment ?? '',
    }))
  }, [soapNotesData])

  const singleNote = notes[0] ?? null
  const isSoapContextResolving =
    !shouldSkipSoapQuery &&
    !soapNotesData &&
    (isLoadingSoapNotes || isFetchingSoapNotes || isSoapNotesUninitialized)

  const [expandedSections, setExpandedSections] = useState<Set<keyof NoteFormValues>>(() =>
    defaultExpanded ? new Set(sectionKeys) : new Set(),
  )
  const [draftState, setDraftState] = useState<NoteDraftState>(() => createEmptyDraftState())
  const [sectionSaveStatuses, setSectionSaveStatuses] = useState<SectionSaveStatuses>(() =>
    createEmptySectionSaveStatuses(),
  )
  const [sectionLastSavedAt, setSectionLastSavedAt] = useState<SectionLastSavedAt>(() =>
    createEmptySectionLastSavedAt(),
  )

  const previousEncounterKeyRef = useRef<string | null>(activeEncounterKey)
  const encounterKeyRef = useRef<string | null>(activeEncounterKey)
  const activeContextRef = useRef<ActiveNoteContext | null>(activeContext)
  const draftStateRef = useRef<NoteDraftState>(draftState)
  const autosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savingIndicatorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastEditedFieldRef = useRef<keyof NoteFormValues | null>(null)
  const isAutosavingRef = useRef(false)
  const rerunAutosaveRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    encounterKeyRef.current = activeEncounterKey
    activeContextRef.current = activeContext
  }, [activeContext, activeEncounterKey])

  useEffect(() => {
    draftStateRef.current = draftState
  }, [draftState])

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  const clearAutosaveTimer = useCallback(() => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current)
      autosaveTimeoutRef.current = null
    }
  }, [])

  const clearSavingIndicatorTimer = useCallback(() => {
    if (savingIndicatorTimeoutRef.current) {
      clearTimeout(savingIndicatorTimeoutRef.current)
      savingIndicatorTimeoutRef.current = null
    }
  }, [])

  const markSectionsStatus = useCallback(
    (fields: (keyof NoteFormValues)[], status: SectionSaveStatus) => {
      if (fields.length === 0) {
        return
      }

      setSectionSaveStatuses((prevStatuses) => {
        const nextStatuses = { ...prevStatuses }
        fields.forEach((field) => {
          nextStatuses[field] = status
        })
        return nextStatuses
      })
    },
    [],
  )

  const markSectionsSaved = useCallback(
    (fields: (keyof NoteFormValues)[]) => {
      if (fields.length === 0) {
        return
      }

      markSectionsStatus(fields, 'saved')
      const formattedTimestamp = DateService.formatDisplayDate(
        new Date().toISOString(),
        true,
        false,
        false,
      ).trim()

      setSectionLastSavedAt((prevTimestamps) => {
        const nextTimestamps = { ...prevTimestamps }
        fields.forEach((field) => {
          nextTimestamps[field] = formattedTimestamp
        })
        return nextTimestamps
      })
    },
    [markSectionsStatus],
  )

  const createPendingDraftSaveSnapshot = useCallback(
    (context: ActiveNoteContext | null): PendingDraftSaveSnapshot | null => {
      if (!context) {
        return null
      }

      return {
        context,
        draft: cloneDraftState(draftStateRef.current),
        preferredField: lastEditedFieldRef.current,
      }
    },
    [],
  )

  const persistDraftForContext = useCallback(
    async (snapshot: PendingDraftSaveSnapshot) => {
      const { context, draft: draftAtStart, preferredField } = snapshot

      if (disableActions || encounterKeyRef.current !== context.encounterKey) {
        return
      }

      if (isAutosavingRef.current) {
        rerunAutosaveRef.current = true
        return
      }

      const fieldToSave = getPreferredSavableField(draftAtStart, preferredField)
      if (!fieldToSave) {
        return
      }

      const savableFieldsAtStart: (keyof NoteFormValues)[] = [fieldToSave]
      const shouldReflectStatus =
        isMountedRef.current && encounterKeyRef.current === context.encounterKey

      if (shouldReflectStatus) {
        markSectionsStatus(savableFieldsAtStart, 'saving')
      }

      isAutosavingRef.current = true

      try {
        const payload = getDraftPayload(draftAtStart, savableFieldsAtStart)

        await saveSoapNote({
          patientPid: context.patientPid,
          encounterUid: context.encounterUid,
          payload,
          skipESignInvalidation: true,
        }).unwrap()

        const latestDraft = cloneDraftState(draftStateRef.current)
        const committedFields = savableFieldsAtStart.filter(
          (field) => latestDraft.values[field] === draftAtStart.values[field],
        )
        const nextDraft: NoteDraftState = {
          values: { ...latestDraft.values },
          dirtyFields: { ...latestDraft.dirtyFields },
          lastSavedValues: { ...latestDraft.lastSavedValues },
        }

        committedFields.forEach((field) => {
          nextDraft.values[field] = payload[field] ?? nextDraft.values[field]
          nextDraft.dirtyFields[field] = false
          nextDraft.lastSavedValues[field] = nextDraft.values[field]
        })

        if (isMountedRef.current && encounterKeyRef.current === context.encounterKey) {
          setDraftState(nextDraft)
          markSectionsSaved(committedFields)
        }
      } catch (error: any) {
        if (isMountedRef.current && encounterKeyRef.current === context.encounterKey) {
          markSectionsStatus(savableFieldsAtStart, 'error')
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Autosave failed',
            description: getAutosaveErrorDescription(error),
          })
        }
      } finally {
        isAutosavingRef.current = false

        if (rerunAutosaveRef.current) {
          rerunAutosaveRef.current = false
          const latestContext = activeContextRef.current
          const rerunSnapshot = createPendingDraftSaveSnapshot(latestContext)
          if (rerunSnapshot && rerunSnapshot.context.encounterKey === context.encounterKey) {
            void persistDraftForContext(rerunSnapshot)
          }
        }
      }
    },
    [
      createPendingDraftSaveSnapshot,
      disableActions,
      markSectionsSaved,
      markSectionsStatus,
      saveSoapNote,
      setNotification,
    ],
  )

  const flushAutosaveNow = useCallback(async () => {
    clearAutosaveTimer()
    clearSavingIndicatorTimer()

    const snapshot = createPendingDraftSaveSnapshot(activeContextRef.current)
    if (!snapshot) {
      return
    }

    await persistDraftForContext(snapshot)
  }, [
    clearAutosaveTimer,
    clearSavingIndicatorTimer,
    createPendingDraftSaveSnapshot,
    persistDraftForContext,
  ])

  useEffect(() => {
    const isEncounterChanged = previousEncounterKeyRef.current !== activeEncounterKey
    previousEncounterKeyRef.current = activeEncounterKey

    clearAutosaveTimer()
    clearSavingIndicatorTimer()

    if (!activeEncounterKey) {
      setDraftState(createEmptyDraftState())
      setSectionSaveStatuses(createEmptySectionSaveStatuses())
      setSectionLastSavedAt(createEmptySectionLastSavedAt())
      lastEditedFieldRef.current = null
      rerunAutosaveRef.current = false
      return
    }

    if (isSoapContextResolving) {
      setDraftState(createEmptyDraftState())
      if (isEncounterChanged) {
        setSectionSaveStatuses(createEmptySectionSaveStatuses())
        setSectionLastSavedAt(createEmptySectionLastSavedAt())
      }
      return
    }

    const serverValues: NoteFormValues = {
      subjective: singleNote?.subjective ?? '',
      objective: singleNote?.objective ?? '',
      assessment: singleNote?.assessment ?? '',
    }

    setDraftState((prevDraft) => {
      if (isEncounterChanged) {
        return {
          values: { ...serverValues },
          dirtyFields: createEmptyDirtyFields(),
          lastSavedValues: { ...serverValues },
        }
      }

      const nextDraft = cloneDraftState(prevDraft)
      sectionKeys.forEach((field) => {
        if (!nextDraft.dirtyFields[field]) {
          nextDraft.values[field] = serverValues[field]
          nextDraft.lastSavedValues[field] = serverValues[field]
        }
      })
      return nextDraft
    })

    if (isEncounterChanged) {
      setSectionSaveStatuses(createEmptySectionSaveStatuses())
      setSectionLastSavedAt(createEmptySectionLastSavedAt())
      lastEditedFieldRef.current = null
      rerunAutosaveRef.current = false
    }
  }, [
    activeEncounterKey,
    clearAutosaveTimer,
    clearSavingIndicatorTimer,
    isSoapContextResolving,
    singleNote?.assessment,
    singleNote?.objective,
    singleNote?.subjective,
  ])

  useEffect(() => {
    clearAutosaveTimer()
    clearSavingIndicatorTimer()

    if (!canMutate || isSoapContextResolving || !activeEncounterKey) {
      return
    }

    const savableFields = getSavableDirtyFields(draftState)
    if (savableFields.length === 0) {
      return
    }

    savingIndicatorTimeoutRef.current = setTimeout(() => {
      if (encounterKeyRef.current !== activeEncounterKey) {
        return
      }

      const pendingFieldToSave = getPreferredSavableField(
        draftStateRef.current,
        lastEditedFieldRef.current,
      )
      if (!pendingFieldToSave) {
        return
      }

      if (isMountedRef.current && encounterKeyRef.current === activeEncounterKey) {
        markSectionsStatus([pendingFieldToSave], 'saving')
      }
    }, NOTES_SAVING_INDICATOR_DELAY_MS)

    const scheduledContext = activeContextRef.current
    autosaveTimeoutRef.current = setTimeout(() => {
      if (!scheduledContext || encounterKeyRef.current !== scheduledContext.encounterKey) {
        return
      }

      const scheduledSnapshot = createPendingDraftSaveSnapshot(scheduledContext)
      if (!scheduledSnapshot) {
        return
      }

      void persistDraftForContext(scheduledSnapshot)
    }, NOTES_AUTOSAVE_DELAY_MS)

    return () => {
      clearAutosaveTimer()
      clearSavingIndicatorTimer()
    }
  }, [
    activeEncounterKey,
    canMutate,
    clearAutosaveTimer,
    clearSavingIndicatorTimer,
    draftState,
    isSoapContextResolving,
    markSectionsStatus,
    createPendingDraftSaveSnapshot,
    persistDraftForContext,
  ])

  useEffect(() => {
    if (!onRegisterLeaveAction) {
      return
    }

    onRegisterLeaveAction(flushAutosaveNow)

    return () => {
      onRegisterLeaveAction(null)
    }
  }, [flushAutosaveNow, onRegisterLeaveAction])

  useEffect(() => {
    return () => {
      clearAutosaveTimer()
      clearSavingIndicatorTimer()
    }
  }, [clearAutosaveTimer, clearSavingIndicatorTimer])

  const toggleSection = useCallback((section: keyof NoteFormValues) => {
    setExpandedSections((prevSections) => {
      const nextSections = new Set(prevSections)

      if (nextSections.has(section)) {
        nextSections.delete(section)
      } else {
        nextSections.add(section)
      }

      return nextSections
    })
  }, [])

  const handleDraftChange = useCallback(
    (field: keyof NoteFormValues, value: string) => {
      if (!canMutate || isSoapContextResolving) {
        return
      }

      lastEditedFieldRef.current = field

      setDraftState((prevDraft) => ({
        values: {
          ...prevDraft.values,
          [field]: value,
        },
        dirtyFields: {
          ...prevDraft.dirtyFields,
          [field]: true,
        },
        lastSavedValues: {
          ...prevDraft.lastSavedValues,
        },
      }))

      setSectionSaveStatuses((prevStatuses) => ({
        ...prevStatuses,
        [field]: sectionLastSavedAt[field] ? 'saved' : 'idle',
      }))
    },
    [canMutate, isSoapContextResolving, sectionLastSavedAt],
  )

  const renderExpandableSections = useCallback(
    (values: NoteFormValues, onChange: (field: keyof NoteFormValues, value: string) => void) => (
      <div className={styles.expandableSections}>
        {sectionKeys.map((section) => {
          const isSectionExpanded = expandedSections.has(section)
          const sectionSaveStatus = sectionSaveStatuses[section]
          const sectionLastSavedTimestamp = sectionLastSavedAt[section]
          const shouldShowLastSavedLabel =
            sectionSaveStatus !== 'saving' &&
            sectionSaveStatus !== 'error' &&
            Boolean(sectionLastSavedTimestamp)
          const sectionStatusLabel = !canMutate
            ? null
            : isSoapContextResolving
            ? null
            : sectionSaveStatus === 'error'
            ? 'Autosave failed'
            : sectionSaveStatus === 'saving'
            ? 'Saving...'
            : shouldShowLastSavedLabel
            ? `Last autosaved at ${sectionLastSavedTimestamp}`
            : 'Input is autosaving'

          return (
            <div key={section} className={styles.sectionBlock}>
              <div className={styles.sectionHeader} onClick={() => toggleSection(section)}>
                <span className={styles.sectionTitle}>{sectionLabels[section]}</span>
                <Icon
                  icon={isSectionExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                  width={24}
                  height={24}
                />
              </div>
              {isSectionExpanded && (
                <div className={styles.sectionContent}>
                  <div className={styles.noteFormGroup}>
                    <TextArea
                      autoSize={{ minRows: 3 }}
                      value={values[section]}
                      onChange={(event) => onChange(section, event.target.value)}
                      placeholder='Type here...This note will SHOW in medical chart.'
                      readOnly={!canMutate || isSoapContextResolving}
                    />
                  </div>
                  {sectionStatusLabel && (
                    <div className={styles.sectionStatusRow}>
                      <span
                        className={classNames(styles.sectionStatusLabel, {
                          [styles.sectionStatusLabelAutosaving]:
                            sectionSaveStatus === 'idle' && !shouldShowLastSavedLabel,
                          [styles.sectionStatusLabelSaving]: sectionSaveStatus === 'saving',
                          [styles.sectionStatusLabelSaved]: shouldShowLastSavedLabel,
                          [styles.sectionStatusLabelError]: sectionSaveStatus === 'error',
                        })}>
                        {sectionStatusLabel}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    ),
    [
      canMutate,
      expandedSections,
      isSoapContextResolving,
      sectionLastSavedAt,
      sectionSaveStatuses,
      toggleSection,
    ],
  )

  if (isSoapContextResolving) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.loadingWrapper}>
          <Spin size='large' />
        </div>
        <div className={styles.proceduresSection}>
          <ProcedureImageList
            selectedPatientId={selectedPatientId}
            selectedEncounterUid={selectedEncounterUid}
            disableActions={disableActions}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.notesList}>
        {renderExpandableSections(draftState.values, handleDraftChange)}
      </div>
      <div className={styles.proceduresSection}>
        <ProcedureImageList
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
          disableActions={disableActions}
        />
      </div>
    </div>
  )
}
